import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { unwrap } from "@/lib/unwrap";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Select } from "@/components/ui/Primitives";
import { BadgeCheck, Plus, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  draft: "amber", in_progress: "blue", completed: "green",
  acknowledged: "violet", cancelled: "slate"
};

export default function Inspections() {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ template_id: "", property_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [i, t, p] = await Promise.all([
        api.get("/inspections").catch(() => ({ data: [] })),
        api.get("/inspections/templates").catch(() => ({ data: [] })),
        api.get("/properties").catch(() => ({ data: [] })),
      ]);
      setItems(unwrap(i.data, "inspections"));
      setTemplates(unwrap(t.data, "templates"));
      setProperties(unwrap(p.data, "properties"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/inspections", {
        template_id: form.template_id,
        property_id: form.property_id,
      });
      toast.success("Inspection started");
      setOpen(false);
      setForm({ template_id: "", property_id: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start inspection");
    } finally { setSaving(false); }
  };

  const downloadPdf = async (id) => {
    try {
      await downloadFile(`/inspections/${id}/pdf`, `inspection-${id.slice(-6)}.pdf`);
    } catch { toast.error("Could not download"); }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Inspections"
        subtitle="Move-in, move-out, and routine condition reports."
        actions={
          <Button onClick={() => setOpen(true)} disabled={templates.length === 0} data-testid="new-inspection-button">
            <Plus className="w-4 h-4"/> New inspection
          </Button>
        }
      />

      {templates.length === 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200 mb-5">
          <div className="text-sm text-amber-900">No inspection templates available yet — new room-by-room checklists are on the way. Need one sooner? Call us at <a href="tel:780-405-2498" className="font-semibold underline">780-405-2498</a>.</div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon={BadgeCheck}
          title="No inspections yet"
          description="Run a condition inspection to document property state and protect deposits."
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((insp) => (
            <Card key={insp.inspection_id} className="p-5" data-testid={`inspection-card-${insp.inspection_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display text-lg font-bold text-slate-900">{insp.template_name || insp.title || "Inspection"}</div>
                  <div className="text-sm text-slate-500 mt-1">{insp.property_name || insp.property_id?.slice(-8)}</div>
                </div>
                <Pill tone={STATUS_TONE[insp.status] || "slate"}>{insp.status?.replace("_"," ")}</Pill>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-sm">
                <span className="text-slate-500">{formatDate(insp.created_at)}</span>
                <Button variant="secondary" onClick={() => downloadPdf(insp.inspection_id)} data-testid={`download-inspection-${insp.inspection_id}`}>
                  <Download className="w-3.5 h-3.5"/> PDF
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New inspection" testid="inspection-modal">
        <form onSubmit={submit} className="space-y-4">
          <Select label="Template" required value={form.template_id} onChange={(e)=>setForm({...form, template_id:e.target.value})} testid="insp-template-select">
            <option value="">Select template</option>
            {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
          </Select>
          <Select label="Property" required value={form.property_id} onChange={(e)=>setForm({...form, property_id:e.target.value})} testid="insp-property-select">
            <option value="">Select property</option>
            {properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
          </Select>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Starting…" : "Start inspection"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
